/**
 * ShareGateLeftPanel
 *
 * Branded left column for the public share dual-column screens (password gate
 * and error states). Thin wrapper that maps `variant` to the matching i18n copy
 * and delegates rendering to the shared SplitScreenBrandPanel.
 *
 * @module components/share/public/shareGate/ShareGateLeftPanel
 */
import { type ReactNode, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { SplitScreenBrandPanel } from '@/components/common/SplitScreenBrandPanel';

type ShareGateVariant = 'password' | 'error';

interface ShareGateLeftPanelProps {
  variant: ShareGateVariant;
}

export const ShareGateLeftPanel = memo(function ShareGateLeftPanel({
  variant,
}: ShareGateLeftPanelProps): ReactNode {
  const { t } = useTranslation();

  // Literal i18n keys (no runtime-assembled keys — keeps extraction tooling working).
  const copy =
    variant === 'password'
      ? {
          heading: t('share.public.password.leftHeading'),
          description: t('share.public.password.leftDescription'),
          footer: t('share.public.password.leftFooter'),
        }
      : {
          heading: t('share.public.error.leftHeading'),
          description: t('share.public.error.leftDescription'),
          footer: t('share.public.error.leftFooter'),
        };

  return (
    <SplitScreenBrandPanel
      heading={copy.heading}
      description={copy.description}
      footer={copy.footer}
    />
  );
});
